import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiCreditCard } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import {
  formatCardNumber,
  formatCep,
  formatCpf,
  formatPhoneBr,
  onlyDigits,
} from "../../helpers/cadastroUtils";
import { validateWizardStep } from "../../helpers/paymentProfileValidation";
import {
  getCards,
  getPagbankErrorMessage,
  replaceCard,
  type PagBankCard,
  type PaymentProfileAddress,
  type PaymentProfileCard,
} from "../../services/pagbank";
import { fetchViaCep } from "../../services/viacep";
import { toast } from "react-toastify";
import { emptyAddr, emptyCard } from "./constants";

const PagamentoPage: React.FC = () => {
  const { getUser } = useAuth();
  const user = getUser();

  const [cards, setCards] = useState<PagBankCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReplace, setShowReplace] = useState(false);
  const [saving, setSaving] = useState(false);

  const [cpf, setCpf] = useState("");
  const [phone, setPhone] = useState("");
  const [addr, setAddr] = useState<PaymentProfileAddress>(emptyAddr);
  const [card, setCard] = useState<PaymentProfileCard>(emptyCard);
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCards();
      setCards(data.cards || []);
      const u = getUser();
      if (u?.phone) {
        setPhone(formatPhoneBr(u.phone));
      }
      if (data.tax_id) {
        setCpf(formatCpf(data.tax_id));
      }
      if (data.address_hint) {
        setAddr({
          ...emptyAddr,
          ...data.address_hint,
          postal_code: formatCep(data.address_hint.postal_code || ""),
          state: (data.address_hint.state || "").slice(0, 2).toUpperCase(),
        });
      }
    } catch {
      toast.error("Não foi possível carregar os dados de pagamento.");
    } finally {
      setLoading(false);
    }
  }, [getUser]);

  useEffect(() => {
    void load();
  }, [load]);

  const tryAdvance = () => {
    const err = validateWizardStep(step, { cpf, phone, addr, card });
    if (err) {
      toast.error(err);
      return;
    }
    setStep((prev) => (prev < 3 ? ((prev + 1) as 1 | 2 | 3) : prev));
  };

  const submitReplace = async () => {
    if (!user?.name || !user?.email) {
      toast.error("Sessão inválida.");
      return;
    }
    const finalErr = validateWizardStep(3, { cpf, phone, addr, card });
    if (finalErr) {
      toast.error(finalErr);
      return;
    }

    const phoneDigits = onlyDigits(phone);
    const em = onlyDigits(card.expiry_month);
    const ey = onlyDigits(card.expiry_year);

    setSaving(true);
    try {
      await replaceCard({
        name: user.name,
        email: user.email,
        cpf: onlyDigits(cpf),
        phone: phoneDigits,
        birth_date: user.birth_date?.split("T")[0],
        address: {
          ...addr,
          postal_code: onlyDigits(addr.postal_code),
          state: addr.state.trim().toUpperCase().slice(0, 2),
          complement: addr.complement || "",
        },
        card: {
          ...card,
          number: onlyDigits(card.number),
          holder_name: card.holder_name.trim(),
          expiry_month: em.padStart(2, "0"),
          expiry_year: ey.length === 2 ? `20${ey}` : ey,
        },
      });
      const hadCard = cards.length > 0;
      toast.success(
        hadCard ? "Cartão atualizado." : "Cartão cadastrado com sucesso.",
      );
      setShowReplace(false);
      setCard(emptyCard);
      setStep(1);
      await load();
    } catch (e: unknown) {
      toast.error(getPagbankErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const primary = cards[0];

  const handleCepChange = async (value: string) => {
    const formatted = formatCep(value);
    const cepDigits = onlyDigits(value);
    setAddr((prev) => ({ ...prev, postal_code: formatted }));

    if (cepDigits.length === 8) {
      try {
        const viaCep = await fetchViaCep(cepDigits);
        setAddr((prev) => ({
          ...prev,
          postal_code: formatted,
          street: viaCep.logradouro || "",
          district: viaCep.bairro || "",
          city: viaCep.localidade || "",
          state: (viaCep.uf || "").slice(0, 2).toUpperCase(),
        }));
        toast.success("Endereço encontrado automaticamente.");
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
        toast.error(
          "Não foi possível localizar esse CEP. Confira e tente novamente.",
        );
      }
    }
  };

  const renderCardForm = () => (
    <div className="space-y-4 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>Cadastro de cartão</span>
        <span>Etapa {step} de 3</span>
      </div>

      {step === 1 && (
        <>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Primeiro, confirme seus dados básicos. Usamos essas informações
            para validar o cartão com o PagBank.
          </p>
          <div>
            <label
              htmlFor="pagamento-cpf"
              className="block text-xs font-medium mb-1"
            >
              CPF
            </label>
            <input
              id="pagamento-cpf"
              className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
              value={cpf}
              onChange={(e) => setCpf(formatCpf(e.target.value))}
              inputMode="numeric"
              autoComplete="off"
              placeholder="000.000.000-00"
            />
          </div>
          <div>
            <label
              htmlFor="pagamento-phone"
              className="block text-xs font-medium mb-1"
            >
              Celular (DDD)
            </label>
            <input
              id="pagamento-phone"
              type="tel"
              className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
              value={phone}
              onChange={(e) => setPhone(formatPhoneBr(e.target.value))}
              inputMode="tel"
              autoComplete="tel"
              placeholder="(11) 99999-9999"
            />
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Agora, informe o endereço de cobrança do cartão.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label
                htmlFor="pagamento-cep"
                className="block text-xs font-medium mb-1"
              >
                CEP
              </label>
              <input
                id="pagamento-cep"
                className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
                value={addr.postal_code}
                onChange={(e) => void handleCepChange(e.target.value)}
                inputMode="numeric"
                autoComplete="postal-code"
                placeholder="00000-000"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium mb-1">Rua</label>
              <input
                className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
                value={addr.street}
                readOnly
                aria-readonly="true"
              />
            </div>
            <div>
              <label
                htmlFor="pagamento-numero"
                className="block text-xs font-medium mb-1"
              >
                Número
              </label>
              <input
                id="pagamento-numero"
                className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
                value={addr.number}
                onChange={(e) =>
                  setAddr({ ...addr, number: e.target.value })
                }
                autoComplete="address-line2"
              />
            </div>
            <div>
              <label
                htmlFor="pagamento-complemento"
                className="block text-xs font-medium mb-1"
              >
                Complemento
              </label>
              <input
                id="pagamento-complemento"
                className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
                value={addr.complement}
                onChange={(e) =>
                  setAddr({ ...addr, complement: e.target.value })
                }
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium mb-1">Bairro</label>
              <input
                className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
                value={addr.district}
                readOnly
                aria-readonly="true"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Cidade</label>
              <input
                className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
                value={addr.city}
                readOnly
                aria-readonly="true"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">UF</label>
              <input
                className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
                maxLength={2}
                value={addr.state}
                readOnly
                aria-readonly="true"
              />
            </div>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Por fim, preencha os dados do cartão de crédito.
          </p>
          <div>
            <label
              htmlFor="pagamento-cc-number"
              className="block text-xs font-medium mb-1"
            >
              Número do cartão
            </label>
            <input
              id="pagamento-cc-number"
              className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
              value={card.number}
              onChange={(e) =>
                setCard({
                  ...card,
                  number: formatCardNumber(e.target.value),
                })
              }
              inputMode="numeric"
              autoComplete="cc-number"
            />
          </div>
          <div>
            <label
              htmlFor="pagamento-cc-name"
              className="block text-xs font-medium mb-1"
            >
              Nome no cartão
            </label>
            <input
              id="pagamento-cc-name"
              className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
              autoComplete="cc-name"
              value={card.holder_name}
              onChange={(e) =>
                setCard({ ...card, holder_name: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label
                htmlFor="pagamento-cc-mm"
                className="block text-xs font-medium mb-1"
              >
                MM
              </label>
              <input
                id="pagamento-cc-mm"
                className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
                value={card.expiry_month}
                onChange={(e) =>
                  setCard({
                    ...card,
                    expiry_month: onlyDigits(e.target.value).slice(0, 2),
                  })
                }
                inputMode="numeric"
                autoComplete="cc-exp-month"
                placeholder="MM"
              />
            </div>
            <div>
              <label
                htmlFor="pagamento-cc-yy"
                className="block text-xs font-medium mb-1"
              >
                AA
              </label>
              <input
                id="pagamento-cc-yy"
                className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
                value={card.expiry_year}
                onChange={(e) =>
                  setCard({
                    ...card,
                    expiry_year: onlyDigits(e.target.value).slice(0, 2),
                  })
                }
                inputMode="numeric"
                autoComplete="cc-exp-year"
                placeholder="AA"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="pagamento-cc-cvv"
              className="block text-xs font-medium mb-1"
            >
              CVV
            </label>
            <input
              id="pagamento-cc-cvv"
              type="password"
              className="w-full min-h-[44px] px-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600"
              value={card.cvv}
              onChange={(e) =>
                setCard({
                  ...card,
                  cvv: onlyDigits(e.target.value).slice(0, 4),
                })
              }
              inputMode="numeric"
              autoComplete="cc-csc"
            />
          </div>
        </>
      )}

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={() => {
            if (step > 1) {
              setStep((prev) => (prev > 1 ? ((prev - 1) as 1 | 2 | 3) : prev));
              return;
            }
            setShowReplace(false);
            setCard(emptyCard);
          }}
          className="flex-1 min-h-[44px] rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium"
        >
          {step === 1 ? "Cancelar" : "Voltar"}
        </button>
        {step < 3 ? (
          <button
            type="button"
            onClick={tryAdvance}
            className="flex-1 min-h-[44px] rounded-lg bg-purple-600 text-sm font-semibold text-white"
          >
            Avançar
          </button>
        ) : (
          <button
            type="button"
            disabled={saving}
            onClick={() => void submitReplace()}
            className="flex-1 min-h-[44px] rounded-lg bg-purple-600 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? "Salvando…" : "Confirmar"}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-lg px-4 py-6 md:py-8">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
        Pagamento
      </h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Gerencie o cartão vinculado à sua conta. Processado com segurança pelo
        PagBank.
      </p>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !primary ? (
        <>
          <div className="mb-4 rounded-xl border border-gray-200 bg-white p-5 text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-purple-100 p-3 dark:bg-purple-900/40">
                <FiCreditCard className="text-2xl text-purple-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  Nenhum cartão cadastrado ainda
                </p>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  Cadastre um cartão para continuar usando o Easyfit sem
                  interrupções. O processo é rápido e seguro.
                </p>
              </div>
            </div>
          </div>
          {renderCardForm()}
        </>
      ) : (
        <>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm mb-4">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-purple-100 dark:bg-purple-900/40 p-3">
                <FiCreditCard className="text-2xl text-purple-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Cartão principal
                </p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {(primary.brand || "Cartão") + " •••• " + primary.last_digits}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Expira em {primary.exp_month}/{primary.exp_year} ·{" "}
                  {primary.holder_name}
                </p>
              </div>
            </div>
          </div>

          {!showReplace ? (
            <button
              type="button"
              onClick={() => setShowReplace(true)}
              className="w-full min-h-[44px] rounded-lg bg-purple-600 text-white font-semibold"
            >
              Trocar cartão
            </button>
          ) : (
            renderCardForm()
          )}

          <p className="mt-6 text-center text-sm">
            <Link to="/perfil" className="text-purple-600 font-medium">
              Voltar ao perfil
            </Link>
          </p>
        </>
      )}
    </div>
  );
};

export default PagamentoPage;
